import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { Minus, Plus, ShoppingBag, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { eur, eur2 } from "@/lib/pricing";
import { useCart } from "@/context/CartContext";

const EMPTY_FORM = { company: "", contact_name: "", email: "", phone: "", notes: "" };

export default function OrderDrawer() {
  const { items, updateBoxes, removeItem, clear, drawerOpen, setDrawerOpen, flyers, removeFlyer } = useCart();
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const totals = useMemo(() => {
    let subtotal = 0;
    let pairs = 0;
    let boxes = 0;
    for (const i of items) {
      const bs = i.boxSize ?? i.product.box_size;
      subtotal += i.boxes * bs * i.product.pair_price;
      pairs += i.boxes * bs;
      boxes += i.boxes;
    }
    return { subtotal, pairs, boxes };
  }, [items]);

  const fabTarget = { x: window.innerWidth - 46, y: window.innerHeight - 46 };

  const submit = async () => {
    if (!form.company.trim() || !form.contact_name.trim() || !form.email.trim()) {
      toast.error("Compila azienda, referente ed email.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/orders`, {
        customer: form,
        items: items.map((i) => ({
          product_id: i.product.id,
          color: i.color,
          size_run: i.sizeRun,
          boxes: i.boxes,
        })),
      });
      setSuccess(res.data);
      clear();
      toast.success(`Preventivo ${res.data.code} inviato. Ti ricontatteremo entro 24h.`);
    } catch (e) {
      toast.error(
        typeof e.response?.data?.detail === "string"
          ? e.response.data.detail
          : "Errore durante l'invio del preventivo."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    setDrawerOpen(false);
    setTimeout(() => setSuccess(null), 400);
  };

  return (
    <>
      {/* Fly-to-cart flyers */}
      <AnimatePresence>
        {flyers.map((f) => (
          <motion.img
            key={f.id}
            src={f.img}
            alt=""
            initial={{ x: f.x - 28, y: f.y - 28, scale: 1, opacity: 1, rotate: 0 }}
            animate={{ x: fabTarget.x - 28, y: fabTarget.y - 28, scale: 0.15, opacity: 0.4, rotate: 25 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.75, ease: [0.5, 0, 0.2, 1] }}
            onAnimationComplete={() => removeFlyer(f.id)}
            className="pointer-events-none fixed left-0 top-0 z-[80] h-14 w-14 rounded-full border-2 border-ink object-cover shadow-xl"
            data-testid="fly-to-cart-badge"
          />
        ))}
      </AnimatePresence>

      {/* Floating cart button */}
      <motion.button
        onClick={() => setDrawerOpen(true)}
        whileTap={{ scale: 0.9 }}
        className="fixed bottom-6 right-6 z-[70] flex h-14 w-14 items-center justify-center rounded-full bg-ink text-white shadow-[0_18px_40px_-10px_rgba(11,12,14,0.5)] transition-colors duration-300 hover:bg-cobalt"
        aria-label="Apri il riepilogo preventivo"
        data-testid="cart-fab"
      >
        <ShoppingBag className="h-5 w-5" />
        <AnimatePresence>
          {items.length > 0 && (
            <motion.span
              key={items.length + totals.boxes}
              initial={{ scale: 0.4 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 500, damping: 18 }}
              className="absolute -right-1 -top-1 flex h-6 min-w-6 items-center justify-center rounded-full bg-cobalt px-1 font-mono text-[10px] font-semibold"
              data-testid="cart-fab-count"
            >
              {items.length}
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Drawer */}
      <AnimatePresence>
        {drawerOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={close}
              className="fixed inset-0 z-[75] bg-ink/50 backdrop-blur-sm"
              data-testid="drawer-backdrop"
            />
            <motion.aside
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 34 }}
              className="fixed bottom-0 right-0 top-0 z-[76] flex w-full max-w-md flex-col bg-white shadow-2xl"
              data-testid="order-drawer"
            >
              <div className="flex items-center justify-between border-b border-black/10 px-6 py-5">
                <div>
                  <h3 className="font-display text-xl font-bold tracking-tight text-ink">Preventivo B2B</h3>
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink/45">
                    Solo box interi per colore
                  </p>
                </div>
                <button
                  onClick={close}
                  className="flex h-9 w-9 items-center justify-center border border-black/10 text-ink transition-colors duration-300 hover:bg-ink hover:text-white"
                  aria-label="Chiudi"
                  data-testid="drawer-close-button"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {success ? (
                <div className="flex flex-1 flex-col items-center justify-center px-8 text-center" data-testid="order-success">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-cobalt font-mono text-white">✓</span>
                  <h4 className="mt-6 font-display text-2xl font-bold tracking-tight text-ink">Preventivo inviato</h4>
                  <p className="mt-2 font-mono text-sm text-cobalt" data-testid="order-success-code">{success.code}</p>
                  <p className="mt-4 max-w-xs text-sm leading-relaxed text-ink/55">
                    {success.total_boxes} box · {success.total_pairs.toLocaleString("it-IT")} paia · Totale {eur(success.subtotal)}.
                    Il nostro team ti ricontatterà entro 24 ore lavorative.
                  </p>
                  <button
                    onClick={close}
                    className="mt-8 bg-ink px-8 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-cobalt"
                    data-testid="order-success-close"
                  >
                    Continua a esplorare
                  </button>
                </div>
              ) : items.length === 0 ? (
                <div className="flex flex-1 flex-col items-center justify-center px-8 text-center" data-testid="order-empty">
                  <ShoppingBag className="h-8 w-8 text-ink/25" />
                  <p className="mt-4 font-mono text-xs uppercase tracking-[0.22em] text-ink/45">
                    Nessun box nel preventivo
                  </p>
                  <p className="mt-2 max-w-xs text-sm text-ink/45">
                    Scegli modello, colore e assortimento taglie dal catalogo e aggiungi un box.
                  </p>
                </div>
              ) : (
                <>
                  <div className="flex-1 overflow-y-auto px-6 py-5">
                    <ul className="space-y-5" data-testid="order-items">
                      {items.map((i) => {
                        const colorData = i.product.colors.find((c) => c.name === i.color) || i.product.colors[0];
                        const bs = i.boxSize ?? i.product.box_size;
                        const linePairs = i.boxes * bs;
                        const lineTotal = linePairs * i.product.pair_price;
                        return (
                          <li key={i.key} className="flex gap-4 border-b border-black/10 pb-5" data-testid={`order-item-${i.product.id}-${i.color.toLowerCase().replace(/ /g, "-")}`}>
                            <img src={colorData.images.profile} alt={`${i.product.name} ${i.color}`} className="h-20 w-16 border border-black/10 object-cover" />
                            <div className="flex flex-1 flex-col">
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <p className="font-display text-sm font-bold text-ink">
                                    {i.product.name} — {i.color}
                                  </p>
                                  <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-ink/45">
                                    {i.product.sku} · {i.sizeRun} · {eur2(i.product.pair_price)}/paio
                                  </p>
                                </div>
                                <button
                                  onClick={() => removeItem(i.key)}
                                  className="text-ink/35 transition-colors duration-300 hover:text-terracotta"
                                  aria-label={`Rimuovi ${i.product.name} ${i.color}`}
                                  data-testid={`order-remove-${i.product.id}-${i.color.toLowerCase().replace(/ /g, "-")}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                              <div className="mt-3 flex items-center justify-between">
                                <div className="flex items-center border border-black/15">
                                  <button
                                    onClick={() => updateBoxes(i.key, -1)}
                                    disabled={i.boxes <= 1}
                                    className="flex h-8 w-8 items-center justify-center text-ink transition-colors duration-200 hover:bg-ink hover:text-white disabled:opacity-30"
                                    aria-label="Togli un box"
                                    data-testid={`order-minus-${i.product.id}`}
                                  >
                                    <Minus className="h-3.5 w-3.5" />
                                  </button>
                                  <span className="w-20 text-center font-mono text-xs" data-testid={`order-boxes-${i.product.id}-${i.color.toLowerCase().replace(/ /g, "-")}`}>
                                    {i.boxes} box
                                  </span>
                                  <button
                                    onClick={() => updateBoxes(i.key, 1)}
                                    className="flex h-8 w-8 items-center justify-center text-ink transition-colors duration-200 hover:bg-ink hover:text-white"
                                    aria-label="Aggiungi un box"
                                    data-testid={`order-plus-${i.product.id}`}
                                  >
                                    <Plus className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                                <p className="font-mono text-sm font-semibold text-ink">{eur(lineTotal)}</p>
                              </div>
                              <p className="mt-1 font-mono text-[10px] text-ink/40">
                                {linePairs} paia ({i.boxes} × {bs}) · minimo 1 box
                              </p>
                            </div>
                          </li>
                        );
                      })}
                    </ul>

                    <div className="mt-6 space-y-2 border border-black/10 bg-paper p-5" data-testid="order-summary">
                      <div className="flex justify-between font-mono text-xs text-ink/60">
                        <span>Box totali</span>
                        <span data-testid="order-total-boxes">{totals.boxes}</span>
                      </div>
                      <div className="flex justify-between font-mono text-xs text-ink/60">
                        <span>Paia totali</span>
                        <span data-testid="order-total-pairs">{totals.pairs.toLocaleString("it-IT")}</span>
                      </div>
                      <div className="flex justify-between border-t border-black/10 pt-2 font-mono text-base font-semibold text-ink">
                        <span>Totale</span>
                        <span data-testid="order-total-price">{eur(totals.subtotal)}</span>
                      </div>
                    </div>

                    <div className="mt-6 space-y-3" data-testid="order-form">
                      {[
                        ["company", "Azienda *", "order-input-company"],
                        ["contact_name", "Nome e cognome referente *", "order-input-name"],
                        ["email", "Email aziendale *", "order-input-email"],
                        ["phone", "Telefono", "order-input-phone"],
                      ].map(([key, placeholder, testid]) => (
                        <input
                          key={key}
                          type={key === "email" ? "email" : "text"}
                          value={form[key]}
                          onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                          placeholder={placeholder}
                          className="h-11 w-full border border-black/15 bg-white px-4 text-sm text-ink placeholder:text-ink/35 focus:border-ink focus:outline-none"
                          data-testid={testid}
                        />
                      ))}
                      <textarea
                        value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        placeholder="Note per il nostro team (tempistiche, seconda scelta colore…)"
                        rows={3}
                        className="w-full border border-black/15 bg-white px-4 py-3 text-sm text-ink placeholder:text-ink/35 focus:border-ink focus:outline-none"
                        data-testid="order-input-notes"
                      />
                    </div>
                  </div>

                  <div className="border-t border-black/10 p-6">
                    <button
                      onClick={submit}
                      disabled={submitting}
                      className="w-full bg-ink px-8 py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:bg-cobalt disabled:opacity-50"
                      data-testid="order-submit-button"
                    >
                      {submitting ? "Invio in corso…" : `Invia preventivo — ${eur(totals.subtotal)}`}
                    </button>
                    <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.18em] text-ink/40">
                      Inviato a Iogamasrls@gmail.com · risposta entro 24h
                    </p>
                  </div>
                </>
              )}
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
